import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { getCourseBySlug } from "@/lib/academy.functions";
import { ModuleCard } from "@/components/academy/ModuleCard";

export const Route = createFileRoute("/aluno/curso/$slug")({
  ssr: false,
  component: CourseView,
});

function CourseView() {
  const { slug } = Route.useParams();
  const [openId, setOpenId] = useState<string | null>(null);
  const { data, isLoading, error } = useQuery({
    queryKey: ["academy", "course", slug],
    queryFn: () => getCourseBySlug({ data: { slug } }),
    staleTime: 30_000,
  });

  if (isLoading) return <p className="text-sm text-muted-foreground">Carregando curso…</p>;
  if (error || !data) return <p className="text-sm text-destructive">Curso não encontrado.</p>;

  const { course, modules } = data;
  const totalLessons = modules.reduce((s: number, m: any) => s + m.lessons.length, 0);
  const completed = modules.reduce((s: number, m: any) => s + m.lessons.filter((l: any) => l.completed).length, 0);
  const pct = totalLessons ? Math.round((completed / totalLessons) * 100) : 0;

  function openModule(id: string) {
    setOpenId((cur) => (cur === id ? null : id));
    setTimeout(() => {
      const el = document.getElementById(`mod-${id}`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 40);
  }

  const activeModule = modules.find((m: any) => m.id === openId) || null;
  const activeIndex = activeModule ? modules.findIndex((m: any) => m.id === activeModule.id) + 1 : 0;

  return (
    <div className="space-y-6">
      <div>
        <Link to="/aluno/cursos" className="text-xs text-muted-foreground hover:text-foreground">← Voltar aos cursos</Link>
        <span className="mt-3 inline-flex items-center rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-[10px] font-semibold uppercase tracking-widest text-primary">
          Trilha completa
        </span>
        <h1 className="mt-2 text-3xl font-extrabold tracking-tight md:text-4xl">{course.title}</h1>
        <p className="mt-1 text-sm text-muted-foreground">{course.description}</p>
        <div className="mt-4 flex items-center gap-3">
          <div className="h-2 flex-1 overflow-hidden rounded-full bg-ink/[0.06]">
            <div className="h-full bg-primary" style={{ width: `${pct}%` }} />
          </div>
          <span className="text-xs text-muted-foreground">{completed}/{totalLessons} · {pct}%</span>
        </div>
      </div>

      {modules.length === 0 ? (
        <div className="card-glass text-sm text-muted-foreground">Este curso ainda não tem módulos publicados.</div>
      ) : (
        <>
          <section>
            <h2 className="mb-3 text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
              Módulos do curso · toque em um card para abrir
            </h2>
            <div className="flex gap-5 overflow-x-auto pb-4 snap-x snap-mandatory md:grid md:grid-cols-2 md:overflow-visible lg:grid-cols-3 xl:grid-cols-4">
              {modules.map((m: any, i: number) => (
                <div
                  key={`card-${m.id}`}
                  className={`w-72 shrink-0 snap-start md:w-auto ${openId === m.id ? "rounded-2xl ring-2 ring-primary/60" : ""}`}
                >
                  <ModuleCard
                    id={m.id}
                    index={i + 1}
                    title={m.title}
                    summary={m.summary}
                    cover_url={m.cover_url}
                    completedLessons={m.lessons.filter((l: any) => l.completed).length}
                    totalLessons={m.lessons.length}
                    onOpen={openModule}
                  />
                </div>
              ))}
            </div>
          </section>

          {activeModule && (() => {
            const m = activeModule;
            const i = activeIndex;
            return (
          <div id={`mod-${m.id}`} className="card-glass scroll-mt-24 transition">
            <div className="mb-3 flex items-center gap-2">
              <span className="inline-flex items-center rounded-md bg-primary px-2 py-1 text-[10px] font-bold uppercase tracking-widest text-primary-foreground">
                MÓDULO {String(i).padStart(2, "0")}
              </span>
              <button
                onClick={() => setOpenId(null)}
                className="ml-auto text-xs text-muted-foreground hover:text-foreground"
              >
                Fechar ✕
              </button>
            </div>
            {m.cover_url && (
              <img src={m.cover_url} alt={m.title} className="mb-3 h-48 w-full rounded-xl object-cover" loading="lazy" />
            )}
            <h3 className="text-lg font-semibold">{m.title}</h3>
            {m.summary && <p className="mt-1 text-sm text-primary">{m.summary}</p>}
            {m.description && <p className="mt-1 text-sm text-muted-foreground">{m.description}</p>}
            {m.video_url && (() => {
              const embed = toEmbedUrl(m.video_url);
              return embed ? (
                <div className="mt-3 aspect-video overflow-hidden rounded-xl border border-ink/10 shadow-[var(--shadow-soft)] bg-black">
                  <iframe src={embed} className="h-full w-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen title={`Introdução ${m.title}`} />
                </div>
              ) : null;
            })()}
            {Array.isArray(m.materials) && m.materials.length > 0 && (
              <div className="mt-3 rounded-xl border border-primary/30 bg-primary/[0.06] p-3">
                <div className="text-xs font-semibold uppercase tracking-wider text-primary">📚 Materiais do módulo</div>
                <ul className="mt-2 grid gap-2 sm:grid-cols-2">
                  {m.materials.map((mat: any, i: number) => (
                    <li key={i} className="rounded-lg border border-ink/10 bg-background/40 p-2 text-xs">
                      <a href={mat.url} target="_blank" rel="noopener" className="font-semibold text-primary hover:underline">
                        🔗 {mat.name}
                      </a>
                      {mat.description && <div className="mt-0.5 text-muted-foreground">{mat.description}</div>}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <ul className="mt-3 divide-y divide-ink/12">
              {m.lessons.length === 0 ? (
                <li className="py-3 text-sm text-muted-foreground">Sem aulas publicadas.</li>
              ) : (
                m.lessons.map((l: any) => (
                  <li key={l.id} className="flex items-center justify-between gap-3 py-3">
                    <div className="min-w-0">
                      <Link to="/aluno/aula/$id" params={{ id: l.id }} className="text-sm font-medium hover:text-primary">
                        {l.completed ? "✓ " : ""}{l.title}
                      </Link>
                      <div className="mt-0.5 text-xs text-muted-foreground">
                        {l.lesson_type} · {l.duration_min || 0} min · +{l.points || 0} pts
                      </div>
                    </div>
                    <Link to="/aluno/aula/$id" params={{ id: l.id }} className="btn-outline text-xs">
                      {l.completed ? "Revisar" : "Assistir"}
                    </Link>
                  </li>
                ))
              )}
            </ul>
          </div>
            );
          })()}
        </>
      )}
    </div>
  );
}

function toEmbedUrl(url: string): string | null {
  try {
    const u = new URL(url);
    if (u.hostname.includes("youtube.com")) {
      const v = u.searchParams.get("v");
      if (v) return `https://www.youtube.com/embed/${v}`;
    }
    if (u.hostname === "youtu.be") return `https://www.youtube.com/embed/${u.pathname.slice(1)}`;
    if (u.hostname.includes("vimeo.com")) {
      const id = u.pathname.split("/").filter(Boolean).pop();
      if (id) return `https://player.vimeo.com/video/${id}`;
    }
    return url;
  } catch { return null; }
}